import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { BADGES } from 'config/badges';

import { Button } from 'components/core/Button';
import { H3 } from 'components/core/Headline';
import { FacilityImage } from 'components/facility/FacilityImage';
import { FacilityImageNotAvailable } from 'components/facility/ImageNotAvailable';
import { Link } from 'components/core/Link';
import { Group } from 'components/core/Page';
import { Paragraph, Text } from 'components/core/Typography';
import { CardBadge } from 'components/core/CardBadge';
import { ClickToShowNumberButton } from 'components/contact/ClickToShowNumberButton';

import FacilityReviewSummary from './FacilityReviewSummary';

export default function FacilityCardHeader({
	average,
	distance,
	facilityUrl,
	facilityImage,
	handleMapAndHoursClick,
	hasReviews,
	name,
	reviewCount,
	tagLine,
	streetAddress,
	city,
	page,
	state,
	zip,
	noFollowFacilityLink,
	fetchSearchCityPhoneNumber,
	hasContactlessBadge,
	lazyLoad,
	imageBadge,
	facilityBadges = [],
	giftCardValue,
}) {
	const tooltipMessage = (badgeName) => {
		if (badgeName === 'giftcard') {
			return BADGES[badgeName].tooltipMessage(giftCardValue);
		}
		return BADGES[badgeName].tooltipMessage;
	};
	return (
		<div className="facility-header">
			<div className="row image-and-details">
				<div
					className={classnames('image-and-badges', {
						'has-badges': hasContactlessBadge,
					})}
				>
					<Link
						className="facility-image-link"
						href={facilityUrl}
						rel={noFollowFacilityLink ? 'nofollow' : ''}
						hideBorder
						productClick
					>
						{!!facilityImage && (
							<FacilityImage
								src={facilityImage}
								alt={`${name} - ${streetAddress}`}
								className="facility-image"
								size="medium"
								lazyLoad={lazyLoad}
							/>
						)}
						{!facilityImage && (
							<FacilityImageNotAvailable size="small" />
						)}
					</Link>
					{!!imageBadge && imageBadge}
					<div
						className={classnames(
							'badges',
							giftCardValue && 'cramped',
						)}
					>
						{facilityBadges.map((badgeName) => {
							const { name, color, icon } = BADGES[badgeName];
							return (
								<CardBadge
									key={badgeName}
									color={color}
									parentPadding="medium"
									icon={icon}
									tooltipMessage={tooltipMessage(badgeName)}
									clickToCloseTooltip
								>
									<Text
										color="white"
										weight="bold"
										size="tiny"
									>
										{name}
									</Text>
								</CardBadge>
							);
						})}
					</div>
				</div>
				<div className="facility-details-links">
					<div className="facility-details">
						<div className="titles">
							<span
								size="small"
								className="facility-title"
							>
								<Link
									href={facilityUrl}
									rel={noFollowFacilityLink ? 'nofollow' : ''}
									hideBorder
									productClick
								>
									<H3
										size="small"
										strong
										itemProp="name"
									>
										{name}
									</H3>
								</Link>
							</span>
							<Paragraph
								light
								className="facility-subtitle"
							>
								{!!tagLine && <Text>{tagLine}</Text>}
								{!tagLine && (
									<span
										className="location-info"
										itemProp="address"
										itemScope
										itemType="http://schema.org/PostalAddress"
									>
										<Text
											rightSpacing
											className="city-state"
										>
											<span
												className="street"
												itemProp="streetAddress"
											>
												{streetAddress}
											</span>
											<span
												className="city"
												itemProp="addressLocality"
											>
												{' '}
												{city}
											</span>
											<span
												className="state"
												itemProp="addressRegion"
											>
												{' '}
												{state}
											</span>
											<span
												className="zip"
												itemProp="postalCode"
											>
												{' '}
												{zip}
											</span>
										</Text>
										<Text>{distance}</Text>
									</span>
								)}
							</Paragraph>
						</div>
					</div>
					{page !== 'paid' && (
						<Group className="facility-header-phone">
							<ClickToShowNumberButton
								showAsLink
								className="phone-number-button"
								outline
								size="standard"
								fetchPhoneNumber={fetchSearchCityPhoneNumber}
							/>
						</Group>
					)}
					<div className="row facility-contact-links">
						{page !== 'paid' && (
							<div className="map-and-hours">
								<Button
									link
									onClick={handleMapAndHoursClick}
								>
									<Text size="small">
										Get map &amp; hours
									</Text>
								</Button>
							</div>
						)}
						{hasReviews && (
							<div className="star-and-promo">
								<FacilityReviewSummary
									rating={average}
									reviewCount={reviewCount}
									facilityUrl={facilityUrl}
									noFollowFacilityLink={noFollowFacilityLink}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

FacilityCardHeader.propTypes = {
	average: PropTypes.number,
	distance: PropTypes.string,
	facilityUrl: PropTypes.string,
	facilityImage: PropTypes.string,
	handleMapAndHoursClick: PropTypes.func,
	name: PropTypes.string,
	reviewCount: PropTypes.number,
	tagLine: PropTypes.string,
	streetAddress: PropTypes.string,
	city: PropTypes.string,
	state: PropTypes.string,
	zip: PropTypes.string,
	page: PropTypes.string,
	hasReviews: PropTypes.bool,
	noFollowFacilityLink: PropTypes.bool,
	fetchSearchCityPhoneNumber: PropTypes.func.isRequired,
	hasContactlessBadge: PropTypes.bool,
	lazyLoad: PropTypes.bool,
	imageBadge: PropTypes.oneOfType([
		PropTypes.node,
		PropTypes.bool, // In case it is using a ternary
	]),
	facilityBadges: PropTypes.arrayOf(PropTypes.string),
	giftCardValue: PropTypes.string,
};

